import { io, Socket } from 'socket.io-client';

// Socket.io client instance
let socket: Socket | null = null;

// Event types for render updates
export interface RenderUpdateEvent {
  renderId: string;
  roomId: string;
  quoteId: string;
  status: 'processing' | 'completed' | 'failed';
  imageUrl?: string;
  error?: string;
  roomName?: string;
  quoteName?: string;
}

/**
 * Get or create the socket connection
 */
export function getSocket(): Socket | null {
  return socket;
}

/**
 * Connect to the WebSocket server with authentication
 */
export function connectSocket(token: string): Socket {
  if (socket?.connected) {
    return socket;
  }

  // Disconnect existing socket if any
  if (socket) {
    socket.disconnect();
  }

  // Determine server URL - in dev, backend runs on different port
  const serverUrl = process.env.NODE_ENV === 'development'
    ? 'http://localhost:6816'
    : window.location.origin;

  socket = io(serverUrl, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
  });

  socket.on('connect', () => {
    console.log('[Socket] Connected:', socket?.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('[Socket] Disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('[Socket] Connection error:', error.message);
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log('[Socket] Reconnected after', attemptNumber, 'attempts');
  });

  socket.on('reconnect_error', (error) => {
    console.error('[Socket] Reconnection error:', error.message);
  });

  return socket;
}

/**
 * Disconnect from the WebSocket server
 */
export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
    console.log('[Socket] Manually disconnected');
  }
}

/**
 * Subscribe to a room (e.g., quote:uuid)
 */
export function subscribeToRoom(room: string): void {
  if (socket?.connected) {
    socket.emit('subscribe', room);
    console.log('[Socket] Subscribed to room:', room);
  }
}

/**
 * Unsubscribe from a room
 */
export function unsubscribeFromRoom(room: string): void {
  if (socket?.connected) {
    socket.emit('unsubscribe', room);
    console.log('[Socket] Unsubscribed from room:', room);
  }
}

/**
 * Check if socket is connected
 */
export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}

/**
 * Add event listener for render updates
 */
export function onRenderUpdate(callback: (data: RenderUpdateEvent) => void): () => void {
  if (!socket) {
    console.warn('[Socket] Cannot add listener - socket not initialized');
    return () => {};
  }

  socket.on('render:updated', callback);

  // Return cleanup function
  return () => {
    socket?.off('render:updated', callback);
  };
}

/**
 * Add a generic event listener
 */
export function onSocketEvent<T>(event: string, callback: (data: T) => void): () => void {
  if (!socket) {
    console.warn('[Socket] Cannot add listener - socket not initialized');
    return () => {};
  }

  socket.on(event, callback);

  return () => {
    socket?.off(event, callback);
  };
}
